import { Grid, useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLink } from '~/hooks/link';
import { DonutChart, type DonutChartData } from '~/pages/dashboard/components/DonutChart';
import {
  formatCompact,
  formatMinutes,
  serializePlayoutFilterParams,
  usePlayoutFilterParams,
  zoneDisplayLabel,
} from '../lib';
import type {
  PlayoutReport__DeviceGroupAggregate,
  PlayoutReport__ZoneAggregate,
} from '../queries/PlayoutReport.generated';

export interface ZoneAndDeviceGroupDonutsProps {
  byZone: ReadonlyArray<PlayoutReport__ZoneAggregate>;
  byDeviceGroup: ReadonlyArray<PlayoutReport__DeviceGroupAggregate>;
  totalDurationMs: number;
}

const formatLegendPercent = (v: number, total: number) => {
  const pct = Math.round((v / total) * 100);
  return v > 0 && pct === 0 ? '<1%' : `${pct}%`;
};

const formatLegendTooltip = (v: number) => `${v.toLocaleString()} m`;

export const ZoneAndDeviceGroupDonuts = ({
  byZone,
  byDeviceGroup,
  totalDurationMs,
}: ZoneAndDeviceGroupDonutsProps) => {
  const { t } = useTranslation();
  const series = useTheme().palette.chartSeries;
  const navigate = useNavigate();
  const link = useLink();
  const { filters, setFilters } = usePlayoutFilterParams();

  const goToAssetType = useCallback(
    (zone: string) => {
      const params = serializePlayoutFilterParams(new URLSearchParams(), filters);
      params.set('assetTypes', zone);
      navigate({ ...link('/playout-reports/by-asset'), search: params.toString() });
    },
    [filters, navigate, link],
  );

  const filterToDeviceGroup = useCallback(
    (deviceGroupId: number) => setFilters({ deviceGroupIds: [deviceGroupId] }, { push: true }),
    [setFilters],
  );

  const zoneChartData = useMemo<DonutChartData[]>(
    () =>
      [...byZone]
        .sort((a, b) => b.durationMs - a.durationMs)
        .map((d, i) => ({
          id: d.zone,
          label: zoneDisplayLabel(d.zone),
          value: formatMinutes(d.durationMs),
          color: series[i % series.length],
          onClick: () => goToAssetType(d.zone),
        })),
    [byZone, goToAssetType, series],
  );

  // 'Other' (when present) is always the last slice, regardless of size.
  const deviceGroupChartData = useMemo<DonutChartData[]>(() => {
    const named = byDeviceGroup
      .filter((d) => d.otherCount == null)
      .sort((a, b) => b.durationMs - a.durationMs);
    const other = byDeviceGroup.find((d) => d.otherCount != null);
    const ordered = other ? [...named, other] : named;
    return ordered.map((d, i) => {
      const { deviceGroupId } = d;
      return {
        id: d.otherCount != null ? '__other__' : d.deviceGroup,
        label:
          d.otherCount != null
            ? t('pages.playout_reports.charts.by_device_group.other_label', { count: d.otherCount })
            : d.deviceGroup,
        value: formatMinutes(d.durationMs),
        color: series[i % series.length],
        ...(deviceGroupId != null && { onClick: () => filterToDeviceGroup(deviceGroupId) }),
      };
    });
  }, [byDeviceGroup, t, filterToDeviceGroup, series]);

  if (zoneChartData.length === 0 && deviceGroupChartData.length === 0) return null;

  const centerValue = formatCompact(formatMinutes(totalDurationMs));

  return (
    <Grid container spacing={2}>
      {zoneChartData.length > 0 && (
        <Grid item xs={12} md={6}>
          <DonutChart
            title={t('pages.playout_reports.charts.by_zone.title')}
            data={zoneChartData}
            centerValue={centerValue}
            centerLabel={t('pages.playout_reports.charts.by_zone.center_label')}
            emptyMessage={t('pages.playout_reports.charts.by_zone.empty')}
            tooltip={t('pages.playout_reports.charts.by_zone.tooltip')}
            legendValueFormatter={formatLegendPercent}
            valueTooltipFormatter={formatLegendTooltip}
          />
        </Grid>
      )}
      {deviceGroupChartData.length > 0 && (
        <Grid item xs={12} md={6}>
          <DonutChart
            title={t('pages.playout_reports.charts.by_device_group.title')}
            data={deviceGroupChartData}
            centerValue={centerValue}
            centerLabel={t('pages.playout_reports.charts.by_device_group.center_label')}
            emptyMessage={t('pages.playout_reports.charts.by_device_group.empty')}
            tooltip={t('pages.playout_reports.charts.by_device_group.tooltip')}
            legendValueFormatter={formatLegendPercent}
            valueTooltipFormatter={formatLegendTooltip}
          />
        </Grid>
      )}
    </Grid>
  );
};
